import React, { useEffect, useMemo, useState } from "react";
import { api, AU_STATES } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, ArrowsClockwise,
  UsersFour, MapPin, LinkedinLogo, EnvelopeSimple, Phone, Buildings,
  Sparkle, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LEAD_TYPES  = ["lead", "partner", "reseller", "integrator", "end_customer", "vendor"];
const LEAD_STATUS = ["new", "contacted", "qualified", "nurture", "disqualified", "converted"];
const LEAD_SIZES  = ["smb", "mid", "enterprise", "government"];
const LEAD_SOURCES= ["linkedin", "referral", "event", "cold", "inbound", "partner_intro", "other"];

const STATUS_COLOR = {
  new:          "text-blue-700 bg-blue-50 border-blue-200",
  contacted:    "text-indigo-700 bg-indigo-50 border-indigo-200",
  qualified:    "text-emerald-700 bg-emerald-50 border-emerald-200",
  nurture:      "text-amber-700 bg-amber-50 border-amber-200",
  disqualified: "text-slate-600 bg-slate-100 border-slate-300",
  converted:    "text-violet-700 bg-violet-50 border-violet-200",
};

const TYPE_COLOR = {
  lead:         "text-slate-700 bg-slate-100",
  partner:      "text-teal-700 bg-teal-50",
  reseller:     "text-orange-700 bg-orange-50",
  integrator:   "text-purple-700 bg-purple-50",
  end_customer: "text-blue-700 bg-blue-50",
  vendor:       "text-fuchsia-700 bg-fuchsia-50",
};

function initForm() {
  return {
    company_name: "", website: "", industry: "",
    lead_type: "lead", size: "", source: "other", status: "new",
    state: "", city: "", address: "",
    contact_name: "", contact_title: "", contact_email: "", contact_phone: "",
    linkedin_url: "", tags: "", assigned_to: "",
    next_action: "", next_action_date: "",
    notes: "",
  };
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_state: {}, by_status: {}, by_type: {} });
  const [loading, setLoading] = useState(true);

  const [filterState, setFilterState] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [q, setQ] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm());

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");

  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverForm, setDiscoverForm] = useState({ criteria: "", state: "", industry: "", count: 10 });
  const [discovering, setDiscovering] = useState(false);
  const [discoverResults, setDiscoverResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState(new Set());

  // Create Discovery Schedule (from a lead → creates a linked Client with discovery_status = discovery_requested)
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [discoveryLead, setDiscoveryLead] = useState(null);
  const [discoveryForm, setDiscoveryForm] = useState({ project_title: "", initial_note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterState) params.state = filterState;
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.lead_type = filterType;
      if (q) params.q = q;
      const [l, s] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/summary"),
      ]);
      setLeads(l.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterState, filterStatus, filterType]);

  const openNew = () => { setEditing(null); setForm(initForm()); setOpen(true); };
  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      ...initForm(),
      ...lead,
      tags: (lead.tags || []).join(", "),
      next_action_date: lead.next_action_date || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      next_action_date: form.next_action_date || null,
    };
    try {
      if (editing) {
        await api.put(`/leads/${editing.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (lead) => {
    if (!confirm(`Delete lead "${lead.company_name}"?`)) return;
    await api.delete(`/leads/${lead.id}`);
    toast.success("Deleted");
    load();
  };

  const convert = async (lead) => {
    if (!confirm(`Convert "${lead.company_name}" to a Client?\n\nThis creates a Client record and marks the lead as converted.`)) return;
    try {
      const r = await api.post(`/leads/${lead.id}/convert-to-client`);
      if (r.data.already_converted) toast.info("Already converted");
      else toast.success("Converted to Client — see the Clients tab");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Convert failed");
    }
  };

  const openCreateDiscovery = (lead) => {
    setDiscoveryLead(lead);
    setDiscoveryForm({
      project_title: lead.company_name ? `${lead.company_name} — Discovery` : "",
      initial_note: "",
    });
    setDiscoveryOpen(true);
  };
  const submitCreateDiscovery = async () => {
    try {
      const r = await api.post(`/leads/${discoveryLead.id}/create-discovery`, discoveryForm);
      if (r.data.already_exists) toast.info("Discovery already exists for this lead");
      else toast.success("Discovery created — schedule the meeting in the Clients tab");
      setDiscoveryOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create discovery");
    }
  };

  const runImport = async () => {
    const rows = importText
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l && !l.startsWith("#"))
      .map(line => {
        const cols = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(c => c.replace(/^"|"$/g, "").trim());
        return {
          company_name: cols[0] || "",
          contact_name: cols[1] || "",
          contact_email: cols[2] || "",
          contact_phone: cols[3] || "",
          website: cols[4] || "",
          state: cols[5] || "",
          city: cols[6] || "",
          industry: cols[7] || "",
          lead_type: cols[8] || "lead",
          source: cols[9] || "other",
          notes: cols[10] || "",
        };
      })
      .filter(r => r.company_name);
    if (!rows.length) { toast.error("No valid rows found"); return; }
    try {
      const r = await api.post("/leads/bulk-import", { rows });
      toast.success(`Imported ${r.data.inserted} — skipped ${r.data.skipped}`);
      setImportOpen(false);
      setImportText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
  };

  const runDiscover = async () => {
    if (!discoverForm.criteria.trim()) { toast.error("Enter a search description"); return; }
    setDiscovering(true);
    setDiscoverResults([]);
    setSelectedResults(new Set());
    try {
      const r = await api.post("/leads/discover-ai", discoverForm);
      setDiscoverResults(r.data.results || []);
      // pre-select all high-confidence
      const preselect = new Set();
      (r.data.results || []).forEach((res, i) => { if (res.confidence === "high") preselect.add(i); });
      setSelectedResults(preselect);
      if (r.data.count === 0) toast.info("No results — try broadening your criteria");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  const addSelectedDiscovered = async () => {
    const picked = discoverResults.filter((_, i) => selectedResults.has(i));
    if (!picked.length) { toast.error("Select at least one result"); return; }
    try {
      const r = await api.post("/leads/discover-ai/add", { results: picked });
      toast.success(`Added ${r.data.inserted} lead${r.data.inserted === 1 ? "" : "s"} — skipped ${r.data.skipped}`);
      setDiscoverOpen(false);
      setDiscoverResults([]);
      setSelectedResults(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };


  const filtered = useMemo(() => {
    if (!q) return leads;
    const s = q.toLowerCase();
    return leads.filter(l =>
      (l.company_name || "").toLowerCase().includes(s) ||
      (l.contact_name || "").toLowerCase().includes(s) ||
      (l.contact_email || "").toLowerCase().includes(s) ||
      (l.notes || "").toLowerCase().includes(s)
    );
  }, [leads, q]);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <PageHeader
        title="Leads"
        subtitle="Prospect + partner + reseller register across Australia. Ingest, qualify, convert to clients."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              data-testid="leads-discover-btn"
              onClick={() => setDiscoverOpen(true)}
              className="bg-gradient-to-r from-violet-50 to-blue-50 border-violet-200 text-violet-700 hover:bg-violet-100"
            >
              <Sparkle size={16} className="mr-2" /> Discover with AI
            </Button>
            <Button variant="outline" data-testid="leads-import-btn" onClick={() => setImportOpen(true)}>
              <ArrowsClockwise size={16} className="mr-2" /> Bulk Import
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="leads-new-btn" onClick={openNew}>
                  <Plus size={16} className="mr-2" /> New Lead
                </Button>
              </DialogTrigger>
              <LeadDialog editing={editing} form={form} setForm={setForm} onSave={save} onEnriched={load} />
            </Dialog>
          </div>
        }
      />

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total leads" value={summary.total} icon={<UsersFour size={20} />} tone="slate" />
        <StatCard label="New" value={summary.by_status?.new || 0} tone="blue" />
        <StatCard label="Qualified" value={summary.by_status?.qualified || 0} tone="emerald" />
        <StatCard label="Converted" value={summary.by_status?.converted || 0} tone="violet" />
      </div>

      {/* AU state chips */}
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
          <MapPin size={14} /> Filter by Australian state
        </div>
        <div className="flex flex-wrap gap-2">
          <StateChip active={filterState === ""} count={summary.total} label="All" onClick={() => setFilterState("")} />
          {AU_STATES.map(s => (
            <StateChip
              key={s.code}
              active={filterState === s.code}
              count={summary.by_state?.[s.code] || 0}
              label={s.code}
              onClick={() => setFilterState(filterState === s.code ? "" : s.code)}
            />
          ))}
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Company, contact, email, notes…"
              className="pl-9"
              data-testid="leads-search"
            />
          </div>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="leads-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Type</Label>
          <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="leads-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {LEAD_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Company</th>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">State</th>
              <th className="text-left px-4 py-3 font-semibold">Contact</th>
              <th className="text-left px-4 py-3 font-semibold">Next action</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                No leads yet — click <b>New Lead</b> or <b>Bulk Import</b> to add prospects.
              </td></tr>
            )}
            {filtered.map(l => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    <Buildings size={14} className="text-slate-400" />
                    {l.company_name}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    {l.industry && <span>{l.industry}</span>}
                    {l.city && <span>{l.city}</span>}
                    {l.website && (
                      <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        website
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${TYPE_COLOR[l.lead_type] || "bg-slate-100"}`}>
                    {(l.lead_type || "lead").replace("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded border font-medium ${STATUS_COLOR[l.status] || "bg-slate-100 border-slate-200"}`}>
                    {l.status}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{l.state || "—"}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-900">{l.contact_name || "—"}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    {l.contact_email && <a href={`mailto:${l.contact_email}`} className="flex items-center gap-1 hover:text-blue-600"><EnvelopeSimple size={11} /> {l.contact_email}</a>}
                    {l.contact_phone && <span className="flex items-center gap-1"><Phone size={11} /> {l.contact_phone}</span>}
                    {l.linkedin_url && <a href={l.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600"><LinkedinLogo size={11} /></a>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {l.next_action ? (
                    <div>
                      <div className="text-slate-800">{l.next_action}</div>
                      {l.next_action_date && <div className="text-slate-500">{l.next_action_date}</div>}
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => openCreateDiscovery(l)}
                      data-testid={`lead-create-discovery-${l.id}`}
                      title="Create a new Discovery Schedule (creates a Client + starts the discovery workflow). You can create as many discoveries as needed over time."
                      className="bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
                    >
                      <Sparkle size={12} className="mr-1" /> {l.status === "converted" ? "New Discovery" : "Create Discovery"}
                    </Button>
                    {l.status === "converted" && (
                      <span
                        className="text-[10px] font-mono uppercase text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded"
                        title="A discovery job was created for this lead — manage it from the Clients tab. Click 'New Discovery' above to spawn another one."
                        data-testid={`lead-discovery-created-${l.id}`}
                      >
                        Discovery job created{l.converted_at ? ` on ${new Date(l.converted_at).toLocaleDateString()}` : ""}
                      </span>
                    )}
                    <Button
                      size="sm" variant="outline"
                      onClick={() => openEdit(l)}
                      data-testid={`lead-edit-${l.id}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => del(l)}
                      data-testid={`lead-delete-${l.id}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI discovery dialog */}
      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="leads-discover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} className="text-violet-600" /> Discover leads with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              ⚠️ AI-suggested plausibles only. Companies proposed here are <b>real, well-known AU firms</b> Claude has knowledge of — but details (website, size, contacts) need verification before outreach.
            </div>
            <div>
              <Label className="text-xs">Search description *</Label>
              <Textarea
                rows={2}
                value={discoverForm.criteria}
                onChange={(e) => setDiscoverForm({ ...discoverForm, criteria: e.target.value })}
                placeholder="e.g. large hospitals in Sydney NSW that would need enterprise network refresh"
                data-testid="discover-criteria"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">State (optional)</Label>
                <Select value={discoverForm.state || "any"} onValueChange={(v) => setDiscoverForm({ ...discoverForm, state: v === "any" ? "" : v })}>
                  <SelectTrigger data-testid="discover-state" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any state</SelectItem>
                    {AU_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} — {s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Industry hint (optional)</Label>
                <Input
                  value={discoverForm.industry}
                  onChange={(e) => setDiscoverForm({ ...discoverForm, industry: e.target.value })}
                  placeholder="Healthcare / MSP / Retail…"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Max results</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={discoverForm.count}
                  onChange={(e) => setDiscoverForm({ ...discoverForm, count: parseInt(e.target.value) || 10 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={runDiscover} disabled={discovering} data-testid="discover-run">
                <Sparkle size={14} className="mr-1.5" /> {discovering ? "Searching…" : "Ask Claude"}
              </Button>
            </div>

            {discoverResults.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="discover-results">
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                  <div className="text-xs font-medium text-slate-700">
                    {discoverResults.length} results — {selectedResults.size} selected
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedResults(new Set(discoverResults.map((_, i) => i)))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      onClick={() => setSelectedResults(new Set())}
                      className="text-xs text-slate-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {discoverResults.map((r, i) => (
                    <label
                      key={i}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 ${selectedResults.has(i) ? "bg-blue-50/50" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedResults.has(i)}
                        onChange={(e) => {
                          const next = new Set(selectedResults);
                          if (e.target.checked) next.add(i); else next.delete(i);
                          setSelectedResults(next);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-slate-900">{r.company_name}</div>
                          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${
                            r.confidence === "high" ? "bg-emerald-100 text-emerald-700" :
                            r.confidence === "low" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            {r.confidence || "medium"}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          {r.industry && <span>{r.industry}</span>}
                          {r.state && <span>· {r.state}</span>}
                          {r.city && <span>· {r.city}</span>}
                          {r.size && <span>· {r.size}</span>}
                        </div>
                        {r.network_angle && (
                          <div className="text-xs text-slate-700 mt-1 italic">{r.network_angle}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <div className="bg-slate-50 px-3 py-2 flex justify-end border-t border-slate-200">
                  <Button size="sm" onClick={addSelectedDiscovered} data-testid="discover-add-selected">
                    Add {selectedResults.size} to leads
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="leads-import-dialog">
          <DialogHeader><DialogTitle>Bulk import leads</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Paste rows below — one lead per line, comma- or tab-separated.
              Column order: <code className="text-xs bg-slate-100 px-1 rounded">company, contact_name, contact_email, phone, website, state, city, industry, lead_type, source, notes</code>.
              Duplicates (same company + email) are skipped.
            </p>
            <Textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={12}
              placeholder={"Acme IT Pty Ltd, Sarah Kelly, sarah@acmeit.com.au, +61 2 9000 0000, acmeit.com.au, NSW, Sydney, MSP, partner, referral, Met at AusCERT 2026"}
              className="font-mono text-xs"
              data-testid="leads-import-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={runImport} data-testid="leads-import-submit">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create Discovery Schedule dialog */}
      <Dialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}>
        <DialogContent className="max-w-lg" data-testid="lead-discovery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={16} className="text-violet-600" />
              Create Discovery · {discoveryLead?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              This creates a linked <b>Client</b> in the Clients tab with status <span className="font-mono text-violet-700">discovery_requested</span>.
              From there you'll schedule the actual meeting — that will push a Discovery job to PSDoc for the presale engineer.
            </p>
            <div>
              <Label className="text-xs">Project working title</Label>
              <Input
                value={discoveryForm.project_title}
                onChange={(e) => setDiscoveryForm({ ...discoveryForm, project_title: e.target.value })}
                placeholder="e.g. Branch SD-WAN refresh"
                className="mt-1"
                data-testid="discovery-title"
              />
            </div>
            <div>
              <Label className="text-xs">Anything already known (optional)</Label>
              <Textarea
                rows={3}
                value={discoveryForm.initial_note}
                onChange={(e) => setDiscoveryForm({ ...discoveryForm, initial_note: e.target.value })}
                placeholder="Anything from the lead conversation the presale engineer should know before the meeting…"
                className="mt-1"
                data-testid="discovery-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscoveryOpen(false)}>Cancel</Button>
            <Button onClick={submitCreateDiscovery} data-testid="discovery-submit" className="bg-violet-600 hover:bg-violet-700 text-white">
              Create Discovery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadDialog({ editing, form, setForm, onSave, onEnriched }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [enriching, setEnriching] = React.useState(false);
  const [enrichment, setEnrichment] = React.useState(null);

  const runEnrich = async () => {
    if (!editing) { toast.error("Save the lead first, then click Enrich"); return; }
    if (!form.company_name?.trim()) { toast.error("Company name required"); return; }
    setEnriching(true);
    try {
      const r = await api.post(`/leads/${editing.id}/enrich-ai`);
      setEnrichment(r.data.enrichment);
      toast.success("AI enrichment ready — review and apply");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const applyEnrichment = async (parts) => {
    if (!editing || !enrichment) return;
    const payload = {};
    if (parts.industry) payload.industry = enrichment.industry;
    if (parts.size)     payload.size = enrichment.size === "unknown" ? "" : enrichment.size;
    if (parts.opener)   payload.next_action = `Reach out — ${enrichment.suggested_opener || ""}`.slice(0, 500);
    if (parts.notes)    payload.notes_append =
      `Industry: ${enrichment.industry}\nSize: ${enrichment.size}\nLikely stack: ${(enrichment.likely_tech_stack||[]).join(", ")}\nPain points: ${(enrichment.network_pain_points||[]).join(" | ")}\nRationale: ${enrichment.rationale}`;

    try {
      await api.post(`/leads/${editing.id}/apply-enrichment`, payload);
      // reflect the applied changes in the local form immediately
      setForm(f => ({
        ...f,
        ...(payload.industry !== undefined ? { industry: payload.industry } : {}),
        ...(payload.size !== undefined ? { size: payload.size } : {}),
        ...(payload.next_action !== undefined ? { next_action: payload.next_action } : {}),
      }));
      toast.success("Applied to lead");
      onEnriched && onEnriched();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lead-dialog">
      <DialogHeader>
        <div className="flex items-center justify-between pr-6">
          <DialogTitle>{editing ? "Edit lead" : "New lead"}</DialogTitle>
          {editing && (
            <Button
              size="sm"
              variant="outline"
              onClick={runEnrich}
              disabled={enriching}
              data-testid="lead-enrich-ai-btn"
              className="bg-gradient-to-r from-violet-50 to-blue-50 border-violet-200 text-violet-700 hover:bg-violet-100"
            >
              <Sparkle size={14} className="mr-1.5" />
              {enriching ? "Enriching…" : "Enrich with AI"}
            </Button>
          )}
        </div>
      </DialogHeader>

      {enrichment && (
        <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-3 space-y-3" data-testid="lead-ai-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-violet-800">
              <Sparkle size={14} /> AI-drafted enrichment
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-white border border-violet-200 text-violet-700">
                confidence: {enrichment.confidence || "medium"}
              </span>
            </div>
            <button
              onClick={() => setEnrichment(null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Dismiss
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <EnrichBlock label="Industry" value={enrichment.industry} apply={() => applyEnrichment({ industry: true })} />
            <EnrichBlock label="Size" value={enrichment.size} apply={() => applyEnrichment({ size: true })} />
          </div>

          {enrichment.likely_tech_stack?.length > 0 && (
            <div>
              <div className="text-[11px] font-medium text-violet-800 uppercase mb-1">Likely stack</div>
              <div className="flex flex-wrap gap-1.5">
                {enrichment.likely_tech_stack.map((t, i) => (
                  <span key={i} className="text-[11px] bg-white border border-violet-200 rounded px-2 py-0.5 text-violet-800">{t}</span>
                ))}
              </div>
            </div>
          )}

          {enrichment.network_pain_points?.length > 0 && (
            <div>
              <div className="text-[11px] font-medium text-violet-800 uppercase mb-1">Likely pain points</div>
              <ul className="text-xs text-slate-700 list-disc list-inside space-y-0.5">
                {enrichment.network_pain_points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          {enrichment.suggested_opener && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-medium text-violet-800 uppercase">Suggested opener</div>
                <Button size="sm" variant="outline" onClick={() => applyEnrichment({ opener: true })} className="h-7 text-xs" data-testid="lead-apply-opener">
                  <Check size={12} className="mr-1" /> Use as next action
                </Button>
              </div>
              <div className="text-xs text-slate-700 bg-white border border-violet-200 rounded p-2 leading-relaxed whitespace-pre-wrap">
                {enrichment.suggested_opener}
              </div>
            </div>
          )}

          {enrichment.rationale && (
            <div className="text-[11px] text-slate-600 italic border-t border-violet-200 pt-2">
              <b>Rationale:</b> {enrichment.rationale}
            </div>
          )}

          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => applyEnrichment({ industry: true, size: true, notes: true })} data-testid="lead-apply-all">
              <Check size={12} className="mr-1" /> Apply all to lead notes
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Company *" span={2}>
          <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="lead-field-company" />
        </Field>
        <Field label="Website"><Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="acmeit.com.au" data-testid="lead-field-website" /></Field>
        <Field label="Industry"><Input value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Healthcare / Retail / MSP…" /></Field>

        <Field label="Type">
          <Select value={form.lead_type} onValueChange={(v) => set("lead_type", v)}>
            <SelectTrigger data-testid="lead-field-type"><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="lead-field-status"><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        <Field label="Size">
          <Select value={form.size || "unset"} onValueChange={(v) => set("size", v === "unset" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {LEAD_SIZES.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Source">
          <Select value={form.source} onValueChange={(v) => set("source", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        <Field label="State">
          <Select value={form.state || "none"} onValueChange={(v) => set("state", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="lead-field-state"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {AU_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} — {s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="City / Suburb"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>

        <Field label="Address" span={2}><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>

        <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="lead-field-contact-name" /></Field>
        <Field label="Contact title"><Input value={form.contact_title} onChange={(e) => set("contact_title", e.target.value)} placeholder="Head of IT / CTO…" /></Field>
        <Field label="Contact email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} type="email" data-testid="lead-field-contact-email" /></Field>
        <Field label="Contact phone"><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
        <Field label="LinkedIn URL" span={2}><Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" /></Field>

        <Field label="Tags (comma separated)" span={2}>
          <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="fortinet, msp, warm" />
        </Field>

        <Field label="Assigned to (email)"><Input value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} placeholder="sales@…" /></Field>
        <Field label="Next action date"><Input type="date" value={form.next_action_date || ""} onChange={(e) => set("next_action_date", e.target.value)} /></Field>
        <Field label="Next action" span={2}><Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="Send capability deck / schedule intro call…" /></Field>

        <Field label="Notes" span={2}>
          <Textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="lead-field-notes" />
        </Field>
      </div>
      <DialogFooter>
        <Button onClick={onSave} data-testid="lead-save-btn">{editing ? "Save changes" : "Add lead"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function EnrichBlock({ label, value, apply }) {
  return (
    <div className="bg-white border border-violet-200 rounded p-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase text-violet-800 font-medium">{label}</div>
        <button onClick={apply} className="text-[10px] text-blue-700 hover:underline">Apply →</button>
      </div>
      <div className="text-sm text-slate-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function StatCard({ label, value, tone = "slate", icon }) {
  const tones = {
    slate:   "text-slate-700 border-slate-200",
    blue:    "text-blue-700 border-blue-200 bg-blue-50/40",
    emerald: "text-emerald-700 border-emerald-200 bg-emerald-50/40",
    violet:  "text-violet-700 border-violet-200 bg-violet-50/40",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider">{label}</div>
        {icon}
      </div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function StateChip({ active, count, label, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`leads-state-chip-${label}`}
      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
        active
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:text-blue-700"
      }`}
    >
      {label}
      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
        {count}
      </span>
    </button>
  );
}
